import type {RuntimeEnvironment} from "@common-core/web-component-runtime/runtime";
import {consume, provide} from "@lit/context";
import {Task} from "@lit/task";
import {LitElement, html} from "lit";
import {customElement} from "lit/decorators.js";
import {unsafeStatic} from "lit/static-html.js";

import type {BridgeBarConfig} from "../config";
import {BridgeBarConfigContext} from "../config";
import {ROOT_TAG} from "../globals";
import type {Environment} from "../runtime";
import {RuntimeContext} from "../runtime";
import {buildBridgeBarApiUrl} from "./buildBridgeBarApiUrl";
import {BridgeBarDataContext} from "./BridgeBarDataContext";
import type {BridgeBarDataContextValue, BridgeUser} from "./BridgeBarData";
import {bridgeApiMockData} from "./bridgeApiMockData";

const tagName = `${ROOT_TAG}-data-provider`;
export const BridgeBarDataProvider = unsafeStatic(tagName);

/**
 * BridgeBarDataProviderElement fetches user details from the Bridge Bar API
 * and provides the result via BridgeBarDataContext to all descendant consumers.
 *
 * The provider always renders `<slot></slot>` regardless of fetch status —
 * blocking children on API status would prevent keep-alive initialization.
 */
@customElement(tagName)
export class BridgeBarDataProviderElement extends LitElement {
  @consume({context: RuntimeContext, subscribe: true})
  runtimeEnvironment: RuntimeEnvironment<Environment, unknown>;

  @consume({context: BridgeBarConfigContext, subscribe: true})
  private bridgeBarConfig: BridgeBarConfig | undefined;

  @provide({context: BridgeBarDataContext})
  bridgeBarData: BridgeBarDataContextValue = {status: "pending"};

  private readonly fetchUserData = new Task<
    readonly [
      RuntimeEnvironment<Environment, unknown> | undefined,
      BridgeBarConfig | undefined
    ]
  >(this, {
    args: () => [this.runtimeEnvironment, this.bridgeBarConfig] as const,

    task: async ([runtimeEnvironment, config]) => {
      if (!runtimeEnvironment?.environment?.bridgeBarApiUrl) {
        // Runtime not yet initialized — stay pending
        return;
      }
      // `?? false` guards an absent config context — deref-ing an undefined
      // `config` would strand the Task in `pending` rather than the `error`
      // state NFR-5 intends.
      if (config?.useMockData ?? false) {
        this.bridgeBarData = {status: "complete", data: bridgeApiMockData};
        return;
      }
      const {environment, runtime} = runtimeEnvironment;
      const partitionQueryParameter = runtime.partition
        .toLowerCase()
        .includes("non-prod")
        ? "NON_PROD"
        : "PROD";

      const url = buildBridgeBarApiUrl(
        environment.bridgeBarApiUrl,
        partitionQueryParameter,
        config
      );
      const correlationId = crypto.randomUUID();

      let response: Response;
      try {
        response = await fetch(url, {
          credentials: "include",
          mode: "cors",
          headers: {
            "Accept": "application/vnd.coxauto.v1+json",
            "X-CoxAuto-Correlation-Id": correlationId
          }
        });
      } catch (networkError) {
        this.bridgeBarData = {
          status: "error",
          error:
            networkError instanceof Error
              ? networkError
              : new Error(String(networkError))
        };
        return;
      }

      if (!response.ok) {
        this.bridgeBarData = {
          status: "error",
          error: new Error(
            `Failed to fetch user details: ${response.status} ${response.statusText}`
          )
        };
        return;
      }

      let data: BridgeUser;
      try {
        data = (await response.json()) as BridgeUser;
      } catch (parseError) {
        this.bridgeBarData = {
          status: "error",
          error:
            parseError instanceof Error
              ? parseError
              : new Error(String(parseError))
        };
        return;
      }

      this.bridgeBarData = {status: "complete", data};
    }
  });

  render() {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: BridgeBarDataProviderElement;
  }
}
